
import { Appointment } from '../types';
import { appointments as mockAppointments } from '../data/mockData';
import { toast } from '@/components/ui/sonner';
import { supabase } from '@/integrations/supabase/client';
import { getMockAppointments, saveMockAppointments } from './appointments/mockAppointmentService';
import { baseApiService } from './base/baseApiService';
import { mapSupabaseAppointment, mapToSupabaseAppointment } from './appointments/appointmentMappers';
import { patientService } from './patients/patientService';
import { timeSlotService } from './appointments/timeSlotService';

export const appointmentService = {
  // Get all appointments
  getAll: async (): Promise<Appointment[]> => {
    return baseApiService.retryApiCall(
      async () => {
        // Try to get appointments from Supabase
        const { data: appointmentsData, error } = await supabase
          .from('appointments')
          .select(`
            *,
            patient:patient_id (full_name, email, phone, address, gender, age)
          `)
          .order('created_at', { ascending: false });

        if (error) throw error;
        
        console.log('Fetched appointments from Supabase:', appointmentsData);
        return appointmentsData.map(mapSupabaseAppointment);
      },
      () => {
        const appointments = getMockAppointments();
        console.log('Using mock appointments data:', appointments.length);
        return appointments;
      }
    );
  },
  
  // Get appointments by doctor
  getByDoctor: async (doctorId: string | number): Promise<Appointment[]> => {
    try {
      // Convert number ID to string if needed
      const doctorIdString = doctorId.toString();
      
      // Get appointments from Supabase
      const { data: appointmentsData, error } = await supabase
        .from('appointments')
        .select(`
          *,
          patient:patient_id (full_name, email, phone, address, gender, age)
        `)
        .eq('doctor_id', doctorIdString);
      
      if (error) throw error;
      
      console.log('Fetched appointments for doctor:', appointmentsData);
      return appointmentsData.map(mapSupabaseAppointment);
    } catch (error: any) {
      // Handle network errors in development with mock data
      if ((import.meta.env.DEV || import.meta.env.MODE === 'development') && baseApiService.useMockData()) {
        console.warn('Using mock appointments data due to error');
        const appointments = getMockAppointments();
        return appointments.filter(app => app.doctorId.toString() === doctorId.toString());
      }
      console.error('Error fetching appointments for doctor:', error);
      throw error;
    }
  },
  
  // Get appointment by ID
  getById: async (id: string): Promise<Appointment> => {
    try {
      const { data: appointment, error } = await supabase
        .from('appointments')
        .select(`
          *,
          patient:patient_id (full_name, email, phone, address, gender, age)
        `)
        .eq('id', id)
        .single();
      
      if (error) throw error;
      
      return mapSupabaseAppointment(appointment);
    } catch (error: any) {
      // Handle network errors in development with mock data
      if ((import.meta.env.DEV || import.meta.env.MODE === 'development') && baseApiService.useMockData()) {
        console.warn('Using mock appointment data due to error');
        const appointments = getMockAppointments();
        const appointment = appointments.find(app => app.id === id);
        if (appointment) {
          return appointment;
        }
        throw new Error('Appointment not found');
      }
      console.error('Error fetching appointment:', error);
      throw error;
    }
  },
  
  // Create new appointment
  create: async (appointmentData: Omit<Appointment, 'id' | 'createdAt'>): Promise<Appointment> => {
    console.log('Creating appointment with data:', appointmentData);
    
    try {
      // First find existing patient or create new one
      const { patientId, error: patientError } = await patientService.findOrCreatePatient(appointmentData);
      
      if (patientError || !patientId) {
        throw patientError || new Error('Failed to process patient data');
      }
      
      // Format data for Supabase
      const supabaseAppointment = mapToSupabaseAppointment(
        appointmentData.doctorId,
        patientId,
        appointmentData.date,
        appointmentData.timeSlot,
        appointmentData.status || 'confirmed'
      );
      
      console.log('Creating appointment with data:', supabaseAppointment);
      
      // Insert appointment
      const { data: newAppointment, error } = await supabase
        .from('appointments')
        .insert(supabaseAppointment)
        .select()
        .single();
        
      if (error) throw error;
      
      console.log('Created appointment in Supabase:', newAppointment);
      
      // Map to our Appointment type
      return {
        id: newAppointment.id,
        doctorId: appointmentData.doctorId,
        date: newAppointment.date,
        timeSlot: newAppointment.time_slot,
        status: newAppointment.status as 'confirmed' | 'cancelled' | 'completed',
        createdAt: newAppointment.created_at,
        // Patient details from appointmentData since they're not in the newly created appointment
        patientName: appointmentData.patientName,
        patientEmail: appointmentData.patientEmail,
        patientPhone: appointmentData.patientPhone,
        patientAddress: appointmentData.patientAddress || '',
        patientGender: appointmentData.patientGender || '',
        patientAge: appointmentData.patientAge || 0
      };
    } catch (error: any) {
      // Handle network errors in development with mock data
      if ((import.meta.env.DEV || import.meta.env.MODE === 'development') && baseApiService.useMockData()) {
        console.warn('Using mock create appointment response due to error');
        const appointments = getMockAppointments();
        
        // Check if the time slot is already booked
        const isTimeSlotBooked = appointments.some(
          app => 
            app.doctorId === appointmentData.doctorId && 
            app.date === appointmentData.date && 
            app.timeSlot === appointmentData.timeSlot && 
            app.status === 'confirmed'
        );
        
        if (isTimeSlotBooked) {
          throw new Error('This time slot is already booked. Please select another time.');
        }
        
        // Generate a unique ID
        const newId = `mock-${Date.now()}`;
        
        // Create a new appointment with the provided data
        const newAppointment: Appointment = {
          id: newId,
          ...appointmentData,
          createdAt: new Date().toISOString()
        };
        
        // Add to mock data
        appointments.push(newAppointment);
        saveMockAppointments(appointments);
        
        console.log('Created mock appointment:', newAppointment);
        return newAppointment;
      }
      console.error('Error creating appointment:', error);
      throw new Error(error.message || 'Failed to create appointment');
    }
  },
  
  // Update appointment
  update: async (id: string, appointmentData: Partial<Appointment>): Promise<Appointment> => {
    try {
      // Format data for Supabase
      const supabaseAppointment: any = {};
      
      if (appointmentData.doctorId) supabaseAppointment.doctor_id = appointmentData.doctorId;
      if (appointmentData.date) supabaseAppointment.date = appointmentData.date;
      if (appointmentData.timeSlot) supabaseAppointment.time_slot = appointmentData.timeSlot;
      if (appointmentData.status) supabaseAppointment.status = appointmentData.status;
      
      // Update appointment
      const { data: updatedAppointment, error } = await supabase
        .from('appointments')
        .update(supabaseAppointment)
        .eq('id', id)
        .select()
        .single();
        
      if (error) throw error;
      
      // Fetch the full appointment with patient details
      const { data: fullAppointment, error: fetchError } = await supabase
        .from('appointments')
        .select(`
          *,
          patient:patient_id (full_name, email, phone, address, gender, age)
        `)
        .eq('id', id)
        .single();
        
      if (fetchError) throw fetchError;
      
      console.log('Updated appointment:', fullAppointment);
      
      return mapSupabaseAppointment(fullAppointment);
    } catch (error: any) {
      // Handle network errors in development with mock data
      if ((import.meta.env.DEV || import.meta.env.MODE === 'development') && baseApiService.useMockData()) {
        console.warn('Using mock update appointment response due to error');
        const appointments = getMockAppointments();
        const index = appointments.findIndex(app => app.id === id);
        
        if (index === -1) {
          throw new Error('Appointment not found');
        }
        
        appointments[index] = { ...appointments[index], ...appointmentData };
        saveMockAppointments(appointments);
        
        console.log('Updated mock appointment:', appointments[index]);
        return appointments[index];
      }
      console.error('Error updating appointment:', error);
      throw new Error(error.message || 'Failed to update appointment');
    }
  },
  
  // Delete appointment
  delete: async (id: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('appointments')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      console.log('Deleted appointment with id:', id);
    } catch (error: any) {
      // Handle network errors in development with mock data
      if ((import.meta.env.DEV || import.meta.env.MODE === 'development') && baseApiService.useMockData()) {
        console.warn('Using mock delete appointment response due to error');
        const appointments = getMockAppointments();
        const filteredAppointments = appointments.filter(app => app.id !== id);
        
        if (appointments.length === filteredAppointments.length) {
          throw new Error('Appointment not found');
        }
        
        saveMockAppointments(filteredAppointments);
        console.log('Deleted mock appointment with id:', id);
        return;
      }
      console.error('Error deleting appointment:', error);
      throw new Error(error.message || 'Failed to delete appointment');
    }
  },
  
  // Check availability
  checkAvailability: async (doctorId: number | string, date: string, timeSlot: string): Promise<boolean> => {
    return timeSlotService.checkAvailability(doctorId, date, timeSlot);
  }
};
